import { useRender } from "@base-ui/react/use-render";
import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/utils";

const noticeDotVariants = cva("shrink-0 rounded-full", {
  variants: {
    size: { sm: "size-1.5", md: "size-2" },
    tone: {
      neutral: "bg-text-muted",
      accent: "bg-accent",
      purple: "bg-purple",
      info: "bg-info",
      success: "bg-success",
      warning: "bg-warning",
      error: "bg-destructive",
    },
  },
  defaultVariants: { tone: "neutral", size: "md" },
});

export type NoticeDotProps = React.ComponentPropsWithoutRef<"span"> &
  VariantProps<typeof noticeDotVariants>;

/** Decorative outcome marker; supply an adjacent text label for its meaning. */
export const NoticeDot = React.forwardRef<HTMLSpanElement, NoticeDotProps>(
  ({ tone, size, className, ...props }, ref) => (
    <span
      {...props}
      ref={ref}
      aria-hidden="true"
      data-slot="notice-dot"
      className={cn(noticeDotVariants({ tone, size }), className)}
    />
  ),
);
NoticeDot.displayName = "NoticeDot";

export type InlineNoticeProps = useRender.ComponentProps<"span"> &
  VariantProps<typeof noticeDotVariants> & {
    /** Metadata can omit the marker; statuses show it by default. */
    showDot?: boolean;
    /** Optional status icon in place of the decorative dot. */
    icon?: React.ReactNode;
  };

/** Compact status text with a decorative semantic dot. No live announcement by default. */
export function InlineNotice({
  tone,
  size = "md",
  className,
  children,
  showDot = true,
  icon,
  render,
  ...props
}: InlineNoticeProps) {
  return useRender({
    defaultTagName: "span",
    render,
    props: {
      "data-slot": "inline-notice",
      ...props,
      className: cn(
        "inline-flex min-w-0 items-baseline font-medium text-text-primary",
        size === "sm" ? "gap-1.5 body-sm" : "gap-2 body-md",
        className,
      ),
      children: (
        <>
          {icon ? (
            <span
              aria-hidden="true"
              className={cn(
                "inline-flex shrink-0 self-center [&>svg]:size-3",
                {
                  neutral: "text-text-muted",
                  accent: "text-accent",
                  purple: "text-purple",
                  info: "text-info",
                  success: "text-success",
                  warning: "text-warning",
                  error: "text-destructive",
                }[tone ?? "neutral"],
              )}
            >
              {icon}
            </span>
          ) : (
            showDot && <NoticeDot tone={tone} size={size} />
          )}
          <span className="min-w-0 [overflow-wrap:anywhere]">{children}</span>
        </>
      ),
    },
  });
}
